import { z } from 'zod';
import type { CareAppointmentLegacyRole, CareAppointmentModality } from './appointments';
import type { CareCallReason } from './care-call-reason';
import type { RoutableCareCallType, WelcomeCareCallType } from './care-call-types';

/**
 * Pelago 1.0 `user_telemedicine_appointments.initialCallType` values as backend2
 * stores them. Not every historical row survives the seam with this column — the
 * mapper treats a missing value as unknown modality, not as video.
 */
export const LEGACY_APPOINTMENT_INITIAL_CALL_TYPE = ['video', 'audio', 'phone'] as const;
export const legacyAppointmentInitialCallTypeSchema = z.enum(LEGACY_APPOINTMENT_INITIAL_CALL_TYPE);
export type LegacyAppointmentInitialCallType = z.infer<
  typeof legacyAppointmentInitialCallTypeSchema
>;

/** Maps 1.0 initial call type to Galapagos visit modality (in-app audio is video). */
export function legacyInitialCallTypeToModality(
  initialCallType: string | null | undefined,
): CareAppointmentModality | null {
  const parsed = legacyAppointmentInitialCallTypeSchema.safeParse(
    initialCallType?.trim().toLowerCase(),
  );
  if (!parsed.success) return null;
  if (parsed.data === 'video' || parsed.data === 'audio') return 'video';
  if (parsed.data === 'phone') return 'phone';
  return null;
}

/** backend2's `AppointmentReason` enum: every value 1.0 wrote to `reason`. */
export const LEGACY_APPOINTMENT_REASON = [
  'pact',
  'mda',
  'counselor_follow_up',
  'coaching_welcome',
  'coaching_follow_up',
  'mat_medical_consult',
  'medical_follow_up',
  'care_coordination',
  'cost_share',
  'family_support',
  'ppc_aftercare',
  'ppc_non_clinical',
  'member_support',
  'welcome_call_30',
] as const;
export const legacyAppointmentReasonSchema = z.enum(LEGACY_APPOINTMENT_REASON);
export type LegacyAppointmentReason = z.infer<typeof legacyAppointmentReasonSchema>;

/**
 * Stands in for the member's programme welcome type, which only the importer
 * (from the member's programmes) or the repair SQL (from `program_definitions`)
 * can resolve.
 */
export const LEGACY_PROGRAMME_WELCOME_CALL_TYPE = 'programme_welcome' as const;

export interface LegacyAppointmentCallTypeMapping {
  callType: RoutableCareCallType | typeof LEGACY_PROGRAMME_WELCOME_CALL_TYPE;
  callReason: CareCallReason | null;
}

/**
 * The Galapagos call type and booking reason each 1.0 reason imports as.
 * The repair migration hand-mirrors these arms, pinned by a contract test.
 */
export const LEGACY_APPOINTMENT_REASON_MAPPING: Readonly<
  Record<LegacyAppointmentReason, LegacyAppointmentCallTypeMapping>
> = {
  coaching_welcome: { callType: LEGACY_PROGRAMME_WELCOME_CALL_TYPE, callReason: null },
  welcome_call_30: { callType: 'welcome_call_low_no', callReason: null },
  coaching_follow_up: { callType: 'follow_up', callReason: null },
  counselor_follow_up: { callType: 'follow_up', callReason: null },
  pact: { callType: 'follow_up', callReason: 'pact' },
  mat_medical_consult: { callType: 'provider_call', callReason: 'mat_medical_consult' },
  medical_follow_up: { callType: 'provider_call', callReason: null },
  mda: { callType: 'provider_call', callReason: 'mda' },
  care_coordination: { callType: 'member_support', callReason: 'care_coordination' },
  member_support: { callType: 'member_support', callReason: null },
  cost_share: { callType: 'member_support', callReason: 'hdhp' },
  family_support: { callType: 'member_support', callReason: 'family_support' },
  ppc_aftercare: { callType: 'progress_purpose_checkin', callReason: 'pnp_aftercare' },
  ppc_non_clinical: { callType: 'progress_purpose_checkin', callReason: 'pnp_non_clinical' },
};

/** The call type a 1.0 row imports as when its reason is absent or unknown. */
export const LEGACY_APPOINTMENT_ROLE_FALLBACK_CALL_TYPE: Readonly<
  Record<CareAppointmentLegacyRole, RoutableCareCallType>
> = {
  physician: 'provider_call',
  quitCoach: 'follow_up',
  careCoordinator: 'member_support',
};

/**
 * The characters stripped from either end of a raw 1.0 reason before it is
 * matched. The repair migration strips the same set with
 * `btrim(…, E' \t\n\r')`; JavaScript's `trim()` would strip every Unicode
 * space and SQL's `trim()` only spaces, so neither is used.
 */
export const LEGACY_REASON_TRIM_CHARACTERS = ' \t\n\r';
const LEGACY_REASON_EDGE_WHITESPACE = /^[ \t\n\r]+|[ \t\n\r]+$/g;

/** A raw 1.0 reason as both the importer and the repair migration match it. */
export function normaliseLegacyReasonToken(reason: string): string {
  return reason.replace(LEGACY_REASON_EDGE_WHITESPACE, '').toLowerCase();
}

export interface LegacyAppointmentCallTypeInput {
  /** The raw 1.0 `reason`. */
  reason: string | null | undefined;
  /** The 1.0 practitioner `type`, already parsed. */
  type: CareAppointmentLegacyRole | null | undefined;
  /** The member's programme welcome type. */
  defaultWelcomeCallType: WelcomeCareCallType;
}

/**
 * A 1.0 appointment's Galapagos call type and reason: the reason's table
 * entry, else the practitioner-type fallback with no reason, else null when
 * neither says what the call was.
 */
export function legacyAppointmentReasonToCallType(
  input: LegacyAppointmentCallTypeInput,
): { callType: RoutableCareCallType; callReason: CareCallReason | null } | null {
  const reason = legacyAppointmentReasonSchema.safeParse(
    input.reason == null ? undefined : normaliseLegacyReasonToken(input.reason),
  );
  if (reason.success) {
    const mapping = LEGACY_APPOINTMENT_REASON_MAPPING[reason.data];
    return {
      callType:
        mapping.callType === LEGACY_PROGRAMME_WELCOME_CALL_TYPE
          ? input.defaultWelcomeCallType
          : mapping.callType,
      callReason: mapping.callReason,
    };
  }
  if (input.type != null) {
    return { callType: LEGACY_APPOINTMENT_ROLE_FALLBACK_CALL_TYPE[input.type], callReason: null };
  }
  return null;
}
